import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  ChevronFirst, ChevronLast, ChevronLeft, ChevronRight, Loader2,
} from 'lucide-react';

/**
 * Pager for server-paged lists. Designed to sit both above and below a list, so a
 * long table doesn't force a scroll back to the top to change page.
 *
 * The one invariant: `total` must be the size of the whole FILTERED result set, not
 * of the rows currently in the DOM. The range readout ("51–100 of 3,412") is only
 * honest if that holds. Usually the server supplies it; where a page legitimately
 * holds the entire set already (because an aggregate needs it), the client can — but
 * the number has to mean the same thing either way.
 */
export interface PaginationProps {
  /** 1-based. */
  page: number;
  pageSize: number;
  /** Total matching rows across all pages, from the server. */
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  /** Compact layout for the top of a list — range + arrows, no page-size picker. */
  compact?: boolean;
  /** Shows a spinner in place of the range while a page is in flight. */
  loading?: boolean;
  /** Noun for the range readout: "3,412 trades". */
  itemLabel?: string;
  className?: string;
}

const DEFAULT_PAGE_SIZES = [25, 50, 100, 250];

export const Pagination: React.FC<PaginationProps> = ({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  compact = false,
  loading = false,
  itemLabel = 'items',
  className = '',
}) => {
  const pageCount = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const clamped = Math.min(Math.max(1, page), pageCount);
  const first = total === 0 ? 0 : (clamped - 1) * pageSize + 1;
  const last = Math.min(clamped * pageSize, total);

  // Local text state so typing "12" isn't fought by a re-render on the first digit.
  const [goto, setGoto] = useState(String(clamped));
  useEffect(() => { setGoto(String(clamped)); }, [clamped]);

  const commitGoto = () => {
    const parsed = parseInt(goto, 10);
    if (isNaN(parsed)) { setGoto(String(clamped)); return; }
    const next = Math.min(Math.max(1, parsed), pageCount);
    setGoto(String(next));
    if (next !== clamped) onPageChange(next);
  };

  const go = (next: number) => {
    const bounded = Math.min(Math.max(1, next), pageCount);
    if (bounded !== clamped) onPageChange(bounded);
  };

  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 text-xs ${className}`}>
      {/* Range */}
      <span className="text-muted-foreground tabular-nums whitespace-nowrap">
        {loading
          ? <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> loading…</span>
          : total === 0
            ? `No ${itemLabel}`
            : <>{first.toLocaleString()}–{last.toLocaleString()} of{' '}
                <span className="font-medium text-foreground">{total.toLocaleString()}</span> {itemLabel}</>}
      </span>

      <div className="flex items-center gap-1 ml-auto">
        <Button
          variant="outline" size="sm" className="h-7 w-7 p-0"
          disabled={clamped <= 1} onClick={() => go(1)} title="First page"
        >
          <ChevronFirst className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="outline" size="sm" className="h-7 w-7 p-0"
          disabled={clamped <= 1} onClick={() => go(clamped - 1)} title="Previous page"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </Button>

        <span className="px-1 text-muted-foreground tabular-nums whitespace-nowrap">
          <input
            value={goto}
            onChange={(e) => setGoto(e.target.value.replace(/[^0-9]/g, ''))}
            onBlur={commitGoto}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitGoto(); }
              if (e.key === 'Escape') setGoto(String(clamped));
            }}
            className="w-10 rounded border bg-background px-1 py-0.5 text-center tabular-nums
                       focus:outline-none focus:ring-1 focus:ring-primary"
            aria-label="Go to page"
          />
          {' / '}{pageCount.toLocaleString()}
        </span>

        <Button
          variant="outline" size="sm" className="h-7 w-7 p-0"
          disabled={clamped >= pageCount} onClick={() => go(clamped + 1)} title="Next page"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="outline" size="sm" className="h-7 w-7 p-0"
          disabled={clamped >= pageCount} onClick={() => go(pageCount)} title="Last page"
        >
          <ChevronLast className="h-3.5 w-3.5" />
        </Button>

        {/* The page-size picker is dropped in compact mode so the top pager stays on
            one line next to filters; the bottom pager carries it. */}
        {!compact && onPageSizeChange && (
          <label className="ml-2 flex items-center gap-1.5 text-muted-foreground">
            <span className="whitespace-nowrap">Per page</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
              className="rounded border bg-background px-1.5 py-1 text-xs
                         focus:outline-none focus:ring-1 focus:ring-primary"
              aria-label="Rows per page"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </label>
        )}
      </div>
    </div>
  );
};

/**
 * Page + page-size state for a paged list.
 *
 * Exists to own the one rule that is easy to forget and invisible when broken:
 * **any change to a filter, the sort, or the page size must reset to page 1**.
 * Narrowing a filter while on page 7 otherwise lands on an empty page, which reads
 * as "no results" when there are plenty.
 *
 * `resetOn` takes the scalar filter/sort values the list is keyed by. They are joined
 * into a single dependency so the effect keeps a fixed dependency-array size no matter
 * how many filters a page has — which means only scalars belong in it, not objects.
 */
export function usePagedList(options: {
  pageSize?: number;
  resetOn: readonly (string | number | boolean | null | undefined)[];
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(options.pageSize ?? 50);

  const resetKey = options.resetOn.join('');
  useEffect(() => { setPage(1); }, [resetKey]);

  const setPageSize = (size: number) => {
    setPageSizeState(size);
    setPage(1);
  };

  return { page, setPage, pageSize, setPageSize };
}

/**
 * Step back a page when the current one comes back empty while results still exist.
 *
 * This is the state you land in after deleting the last row on a page: the server
 * honestly reports zero items for page 4 of a set that now has three pages, and the
 * list renders its "nothing here" message over a set that is not empty at all.
 */
export function useEmptyPageGuard(
  page: number,
  setPage: (page: number) => void,
  itemCount: number,
  total: number,
) {
  useEffect(() => {
    if (itemCount === 0 && total > 0 && page > 1) setPage(page - 1);
  }, [itemCount, total, page, setPage]);
}

/**
 * Guards a paged list against an out-of-order response.
 *
 * Two requests for the same list can be in flight at once - click "next" twice, or
 * change the sort while a page is still loading - and nothing guarantees they come
 * back in order. Whichever lands last wins, so a slow page 2 can overwrite a fast
 * page 3: the rows and the page indicator disagree, and `total` is left holding a
 * stale count that stops the pager on the wrong boundary.
 *
 * Call at the START of a load and commit only while `isCurrent()` holds. The counter
 * lives in a ref rather than in the effect, so it covers loads triggered imperatively
 * (reloading after a delete) as well as those driven by the effect - a `cancelled`
 * flag scoped to the effect would miss the former.
 */
export function useLatestRequest(): () => () => boolean {
  const generation = useRef(0);
  return useCallback(() => {
    const mine = ++generation.current;
    return () => mine === generation.current;
  }, []);
}

/** Debounce a fast-changing value (a search box) so each keystroke isn't a request. */
export function useDebounced<T>(value: T, ms = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}
